import { useQuery } from 'convex/react';
import { useParams } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import { type Column, DataTable } from '../components/DataTable';
import { Empty } from '../components/Empty';
import { FranchiseLink } from '../components/FranchiseLink';
import { Loading } from '../components/Loading';
import { PageShell } from '../components/PageShell';

export default function HistorySeason(): JSX.Element {
  const { year } = useParams<{ year: string }>();
  const yearNum = Number(year);
  const yearArg = Number.isFinite(yearNum) ? { year: yearNum } : ('skip' as const);
  const seasonArg = Number.isFinite(yearNum) ? { season: yearNum } : ('skip' as const);

  const overview = useQuery(api.history.seasons.getSeasonOverview, yearArg);
  const standings = useQuery(api.history.standings.computeForYear, yearArg);
  const brackets = useQuery(api.history.brackets.getForYear, yearArg);
  const matchups = useQuery(api.history.matchups.listForYear, yearArg);
  const rules = useQuery(api.scoringRules.getActiveRules, seasonArg);

  if (!Number.isFinite(yearNum)) {
    return (
      <PageShell title="History">
        <Empty>Invalid year.</Empty>
      </PageShell>
    );
  }
  if (
    overview === undefined ||
    standings === undefined ||
    brackets === undefined ||
    matchups === undefined ||
    rules === undefined
  ) {
    return (
      <PageShell>
        <Loading />
      </PageShell>
    );
  }
  if (overview === null) {
    return (
      <PageShell title={`${yearNum} season`}>
        <Empty>No data for {yearNum}.</Empty>
      </PageShell>
    );
  }

  const standingsCols: Column<(typeof standings)[number]>[] = [
    {
      header: 'Franchise',
      cell: (r) => <FranchiseLink franchiseId={r.franchiseId} name={r.name} />,
    },
    { header: 'Div', cell: (r) => r.division },
    { header: 'W', cell: (r) => r.wins, align: 'right' },
    { header: 'L', cell: (r) => r.losses, align: 'right' },
    { header: 'T', cell: (r) => r.ties, align: 'right' },
    { header: 'PF', cell: (r) => r.pointsFor.toFixed(2), align: 'right' },
    { header: 'PA', cell: (r) => r.pointsAgainst.toFixed(2), align: 'right' },
  ];

  const matchupsCols: Column<(typeof matchups)[number]>[] = [
    { header: 'Wk', cell: (m) => m.week, align: 'right' },
    {
      header: 'Away',
      cell: (m) => <FranchiseLink franchiseId={m.awayFranchiseId} name={m.awayName} />,
    },
    {
      header: 'Score',
      cell: (m) => `${m.awayScore.toFixed(2)} – ${m.homeScore.toFixed(2)}`,
      align: 'right',
    },
    {
      header: 'Home',
      cell: (m) => <FranchiseLink franchiseId={m.homeFranchiseId} name={m.homeName} />,
    },
    { header: 'Type', cell: (m) => (m.isPlayoff ? (m.bracket ?? 'playoff') : 'reg') },
  ];

  return (
    <PageShell title={`${yearNum} season`}>
      {/* Champion banner */}
      <section className="mb-6 border rounded p-3 bg-white">
        <div className="text-sm text-gray-500 mb-1">Champion</div>
        <div className="text-lg font-semibold">
          <FranchiseLink franchiseId={overview.championFranchiseId} name={overview.championName} />
        </div>
        {overview.runnerUpFranchiseId && overview.runnerUpName && (
          <div className="text-sm text-gray-600 mt-2">
            Runner-up:{' '}
            <FranchiseLink
              franchiseId={overview.runnerUpFranchiseId}
              name={overview.runnerUpName}
            />
          </div>
        )}
        {overview.ultimateLoserFranchiseId && overview.ultimateLoserName && (
          <div className="text-sm text-gray-600">
            Ultimate loser:{' '}
            <FranchiseLink
              franchiseId={overview.ultimateLoserFranchiseId}
              name={overview.ultimateLoserName}
            />
          </div>
        )}
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">Regular-season standings</h2>
        <DataTable
          columns={standingsCols}
          rows={standings}
          rowKey={(r) => r.franchiseId}
          empty="No regular-season games recorded."
        />
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">Playoff bracket</h2>
        {brackets.championship.length === 0 && brackets.ultimateLoser.length === 0 ? (
          <Empty>No playoff bracket recorded.</Empty>
        ) : (
          <>
            {brackets.championship.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Championship</h3>
                {brackets.championship.map((r) => (
                  <div key={r.roundNumber} className="mb-2">
                    <div className="text-xs text-gray-500">
                      Round {r.roundNumber} (wk {r.games[0]?.weekStart}–{r.games[0]?.weekEnd})
                    </div>
                    {r.games.map((g, i) => (
                      <div
                        key={`${g.awayFranchiseId}-${g.homeFranchiseId}-${i}`}
                        className="text-sm"
                      >
                        <FranchiseLink franchiseId={g.awayFranchiseId} name={g.awayName} />{' '}
                        {g.awayScore.toFixed(2)} – {g.homeScore.toFixed(2)}{' '}
                        <FranchiseLink franchiseId={g.homeFranchiseId} name={g.homeName} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {brackets.ultimateLoser.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-1">Ultimate loser bracket</h3>
                {brackets.ultimateLoser.map((r) => (
                  <div key={r.roundNumber} className="mb-2">
                    <div className="text-xs text-gray-500">Round {r.roundNumber}</div>
                    {r.games.map((g, i) => (
                      <div
                        key={`${g.awayFranchiseId}-${g.homeFranchiseId}-${i}`}
                        className="text-sm"
                      >
                        <FranchiseLink franchiseId={g.awayFranchiseId} name={g.awayName} />{' '}
                        {g.awayScore.toFixed(2)} – {g.homeScore.toFixed(2)}{' '}
                        <FranchiseLink franchiseId={g.homeFranchiseId} name={g.homeName} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">Matchups</h2>
        <DataTable
          columns={matchupsCols}
          rows={matchups}
          rowKey={(m) => `${m.week}-${m.awayFranchiseId}-${m.homeFranchiseId}`}
          empty="No matchups recorded."
        />
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">Scoring rules</h2>
        {rules ? (
          <div className="text-sm">
            <div className="text-gray-500 mb-1">
              Season {yearNum} active rules ({rules.rules.length}):
            </div>
            <ul className="list-disc list-inside text-xs font-mono">
              {rules.rules.map((r, i) => (
                <li key={`${r.position}.${r.stat}.${i}`}>
                  {r.position}.{r.stat}:{' '}
                  {r.kind === 'linear' ? `${r.pointsPerUnit} pts/unit` : r.kind}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Empty>No scoring rules recorded for {yearNum}.</Empty>
        )}
      </section>

      <section className="mb-6">
        <h2 className="font-semibold mb-2">Season awards</h2>
        <Empty>
          No awards recorded yet. Commissioner UI for awards is planned for a future phase.
        </Empty>
      </section>
    </PageShell>
  );
}
